/**
 * Événements — ce qu'on vous recommande (et pourquoi), ce que vous avez déjà
 * rejoint, le reste des événements ouverts par catégorie, et ceux que VOUS
 * organisez : tout participant peut créer un événement (le premier est offert),
 * la formule Organisateur ouvre le reste.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ExternalLink, MapPin, Plus, Users } from 'lucide-react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Btn, Card, Chip, Input, Modal, PageTitle, SectionLabel, Select, TextArea } from '../../admin/ui.tsx';
import { ProgrammeEditeur } from './ProgrammeEditeur.tsx';
import {
  CATEGORIES_EVENEMENT,
  dateLongue,
  euros,
  heure,
  libelleCategorie,
  type CategorieEvenement,
  type Espace,
  type EspaceOrganisateur,
  type EvenementCree,
  type EvenementOuvert,
  type EvenementRecommande,
} from './types.ts';

export function Evenements({ espace, onChange }: { espace: Espace; onChange: () => Promise<void> }) {
  const [recommandes, setRecommandes] = useState<EvenementRecommande[] | null>(null);
  const [ouverts, setOuverts] = useState<EvenementOuvert[] | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [organisateur, setOrganisateur] = useState<EspaceOrganisateur | null>(null);
  const [categorie, setCategorie] = useState<CategorieEvenement | null>(null);
  const [creer, setCreer] = useState<EvenementCree | 'nouveau' | null>(null);
  const [programmeDe, setProgrammeDe] = useState<EvenementCree | null>(null);

  const charger = async () => {
    const [r, o, g] = await Promise.all([
      appelerApi<{ evenements: EvenementRecommande[] }>('/api/compte/evenements-recommandes'),
      appelerApi<{ evenements: EvenementOuvert[] }>('/api/compte/evenements-ouverts'),
      appelerApi<EspaceOrganisateur>('/api/mes-evenements'),
    ]);
    setRecommandes(r.donnees?.evenements ?? []);
    setOuverts(o.donnees?.evenements ?? []);
    setOrganisateur(g.donnees);
  };
  useEffect(() => {
    void charger();
  }, []);

  const dejaRejoints = new Set(espace.participations.map((p) => p.evenement_id));

  const rejoindre = async (id: string) => {
    setEnCours(id);
    setErreur(null);
    const r = await envoyer(`/api/compte/evenements/${id}/rejoindre`, {});
    setEnCours(null);
    if (r.erreur) {
      setErreur(r.erreur);
      return;
    }
    await Promise.all([charger(), onChange()]);
  };

  const recommandesAffiches = (recommandes ?? []).filter((e) => !dejaRejoints.has(e.id));
  const idsRecommandes = new Set(recommandesAffiches.map((e) => e.id));
  const autres = (ouverts ?? []).filter(
    (e) => !dejaRejoints.has(e.id) && !idsRecommandes.has(e.id) && (!categorie || e.categorie === categorie),
  );

  return (
    <>
      <PageTitle
        title={
          <>
            Mes <em className="text-match-gradient font-serif italic font-medium">événements</em>
          </>
        }
        subtitle="Rejoignez en un clic : votre profil est déjà rempli, vous n’avez rien à retaper."
      />

      {recommandesAffiches.length > 0 && (
        <section className="mb-10">
          <h2 className="text-[18px] font-light text-blanc">À ne pas manquer</h2>
          <p className="mb-4 mt-1 max-w-[640px] text-[13px] leading-relaxed text-fumee">
            Classés d’abord sur les entrepreneurs compatibles déjà inscrits — on n’y va pas pour la
            salle, on y va pour les gens.
          </p>
          <div className="grid gap-4 lg:grid-cols-3">
            {recommandesAffiches.map((e) => (
              <Card key={e.id} className="flex flex-col">
                <p className="flex flex-wrap items-center gap-2 text-[15px] text-blanc">
                  {e.nom}
                  {e.aujourdhui && <Badge filled>Aujourd’hui</Badge>}
                </p>
                <p className="mt-1 text-[13px] text-fumee">
                  {dateLongue(e.debut_le)}
                  {e.lieu_ville ? ` · ${e.lieu_ville}` : ''}
                </p>
                <p className="mt-3 flex-1 text-[13px] leading-relaxed text-blanc">{e.raison}</p>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <span className="text-[12px] text-fumee">
                    {e.jours_avant === 0 ? 'Aujourd’hui' : `Dans ${e.jours_avant} j`} · {e.participants} inscrits
                  </span>
                  <Btn variant="primary" disabled={enCours === e.id} onClick={() => void rejoindre(e.id)}>
                    {enCours === e.id ? '…' : 'Rejoindre'}
                  </Btn>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {espace.participations.length > 0 && (
        <section className="mb-10">
          <SectionLabel>Je suis inscrit</SectionLabel>
          <div className="mt-3 flex flex-col gap-3">
            {espace.participations.map((p) => (
              <Card key={p.participant_id} className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="flex flex-wrap items-center gap-2 text-[15px] text-blanc">
                    {p.nom}
                    {p.aujourdhui && <Badge filled>Aujourd’hui</Badge>}
                  </p>
                  <p className="mt-1 text-[13px] text-fumee">
                    {dateLongue(p.debut_le)} · {[p.lieu_nom, p.lieu_ville].filter(Boolean).join(', ')}
                  </p>
                </div>
                <span className="flex flex-wrap items-center gap-3">
                  <a href={`/mur/${p.slug ?? ''}`} className="text-[13px] text-fumee underline hover:text-blanc">
                    Les murs
                  </a>
                  <a href={`/affiche/${p.slug ?? ''}`} className="text-[13px] text-fumee underline hover:text-blanc">
                    Mon affiche
                  </a>
                  <Link to="/compte/rendez-vous" className="text-[13px] text-fumee underline hover:text-blanc">
                    Qui y sera
                  </Link>
                  <BoutonEntrer participantId={p.participant_id} />
                </span>
              </Card>
            ))}
          </div>
        </section>
      )}

      <MesEvenements organisateur={organisateur} onCreer={() => setCreer('nouveau')} onModifier={(e) => setCreer(e)} onProgramme={(e) => setProgrammeDe(e)} />

      {programmeDe && (
        <Modal title={`Programme — ${programmeDe.nom}`} onClose={() => setProgrammeDe(null)}>
          <ProgrammeEditeur base={`/api/sessions/mes-evenements/${programmeDe.id}`} jourParDefaut={programmeDe.debut_le} />
        </Modal>
      )}

      <section>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <SectionLabel>Ouverts aux inscriptions</SectionLabel>
          <div className="flex flex-wrap gap-2">
            <Chip active={categorie === null} onClick={() => setCategorie(null)}>Tout</Chip>
            {CATEGORIES_EVENEMENT.map((c) => (
              <Chip key={c.cle} active={categorie === c.cle} onClick={() => setCategorie(c.cle)}>
                {c.court}
              </Chip>
            ))}
          </div>
        </div>
        {ouverts === null && <p className="mt-3 text-[14px] text-fumee">Chargement…</p>}
        {ouverts !== null && autres.length === 0 && recommandesAffiches.length === 0 && (
          <p className="mt-3 max-w-[560px] text-[14px] leading-relaxed text-fumee">
            Aucun événement ouvert aux inscriptions libres pour l’instant. Si un organisateur vous a
            donné un code, entrez-le depuis la page d’accueil — votre profil vous suivra
            automatiquement.
          </p>
        )}
        <div className="mt-3 flex flex-col gap-3">
          {autres.map((e) => (
            <Card key={e.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 text-[15px] text-blanc">
                    {e.nom}
                    <Badge>{libelleCategorie(e.categorie, e.sous_categorie)}</Badge>
                    {e.aujourdhui && <Badge filled>Aujourd’hui</Badge>}
                  </p>
                  <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-fumee">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={13} strokeWidth={1.5} />
                      {dateLongue(e.debut_le)} · {heure(e.debut_le)}
                    </span>
                    {(e.lieu_nom || e.lieu_ville) && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={13} strokeWidth={1.5} />
                        {[e.lieu_nom, e.lieu_ville].filter(Boolean).join(', ')}
                      </span>
                    )}
                    <span>· {e.organisation_nom}</span>
                    {e.participants > 0 && (
                      <span className="inline-flex items-center gap-1.5">
                        <Users size={13} strokeWidth={1.5} />
                        {e.participants}
                      </span>
                    )}
                  </p>
                  {e.description && <p className="mt-2 max-w-[520px] text-[13px] leading-relaxed text-fumee">{e.description}</p>}
                </div>
                <Btn variant="primary" disabled={enCours === e.id} onClick={() => void rejoindre(e.id)}>
                  {enCours === e.id ? '…' : 'Rejoindre'}
                </Btn>
              </div>
            </Card>
          ))}
        </div>
        {erreur && (
          <p className="mt-3 text-[13px]" role="alert" style={{ color: 'var(--em-erreur)' }}>
            {erreur}
          </p>
        )}
      </section>

      {creer && organisateur && (
        <FormulaireEvenement
          existant={creer === 'nouveau' ? null : creer}
          organisateur={organisateur}
          onClose={() => setCreer(null)}
          onFini={() => {
            setCreer(null);
            void Promise.all([charger(), onChange()]);
          }}
        />
      )}
    </>
  );
}

/* ————————————————— Ce que j'organise ————————————————— */

function MesEvenements({
  organisateur,
  onCreer,
  onModifier,
  onProgramme,
}: {
  organisateur: EspaceOrganisateur | null;
  onCreer: () => void;
  onModifier: (e: EvenementCree) => void;
  onProgramme: (e: EvenementCree) => void;
}) {
  const [message, setMessage] = useState<string | null>(null);
  if (!organisateur) return null;

  const souscrire = async () => {
    setMessage(null);
    const r = await envoyer<{ url: string }>('/api/mes-evenements/organisateur/souscrire', {});
    if (r.donnees?.url) location.assign(r.donnees.url);
    else setMessage(r.erreur ?? 'Paiement indisponible pour l’instant.');
  };
  const entrer = async () => {
    const r = await envoyer('/api/mes-evenements/organisateur/entrer', {});
    if (r.erreur) setMessage(r.erreur);
    else location.assign('/orga');
  };

  return (
    <section className="mb-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionLabel>J’organise</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {organisateur.payant && (
            <Btn onClick={() => void entrer()}>
              <ExternalLink size={13} strokeWidth={1.5} className="mr-1.5 inline" />
              Ouvrir le back-office
            </Btn>
          )}
          <Btn variant="primary" onClick={onCreer}>
            <Plus size={13} strokeWidth={1.5} className="mr-1.5 inline" />
            Créer un événement
          </Btn>
        </div>
      </div>

      {organisateur.evenements.length === 0 ? (
        <Card className="mt-3">
          <p className="text-[15px] text-blanc">Votre premier événement est offert.</p>
          <p className="mt-2 max-w-[640px] text-[14px] leading-relaxed text-fumee">
            Un afterwork, un match entre entrepreneurs, une matinée de formation : créez-le, ouvrez
            les inscriptions, et les profils compatibles le verront recommandé. Pour en mener
            plusieurs à la fois, ou pour avoir le back-office complet — profil organisateur,
            sponsors, billetterie —, la formule Organisateur est à{' '}
            <span className="text-blanc">{euros(organisateur.prix_mensuel_centimes)} par mois</span>, facturés{' '}
            {euros(organisateur.prix_annuel_centimes)} à l’année.
          </p>
        </Card>
      ) : (
        <div className="mt-3 flex flex-col gap-3">
          {organisateur.evenements.map((e) => (
            <Card key={e.id} className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-[15px] text-blanc">
                  {e.nom}
                  <Badge>{libelleCategorie(e.categorie, e.sous_categorie)}</Badge>
                  <Badge filled={e.statut === 'ouvert' || e.statut === 'en_cours'}>
                    {e.statut === 'ouvert' ? (e.visibilite_publique ? 'Inscriptions ouvertes' : 'Sur invitation') : e.statut}
                  </Badge>
                </p>
                <p className="mt-1 text-[13px] text-fumee">
                  {dateLongue(e.debut_le)} · {heure(e.debut_le)}
                  {e.lieu_ville ? ` · ${e.lieu_ville}` : ''} · {e.participants} inscrit{e.participants > 1 ? 's' : ''}
                </p>
              </div>
              <span className="flex flex-wrap gap-2">
                <a href={`/mur/${e.slug}`} target="_blank" rel="noreferrer" className="rounded-full border border-graphite px-4 py-2 text-[14px] text-fumee transition-colors hover:text-blanc">
                  Les murs
                </a>
                <a href={`/affiche/${e.slug}`} target="_blank" rel="noreferrer" className="rounded-full border border-graphite px-4 py-2 text-[14px] text-fumee transition-colors hover:text-blanc">
                  Affiche à imprimer
                </a>
                <Btn onClick={() => onProgramme(e)}>Programme</Btn>
                <Btn onClick={() => onModifier(e)}>Modifier</Btn>
              </span>
            </Card>
          ))}
        </div>
      )}

      {!organisateur.payant && (
        <Card className="mt-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0 max-w-[640px]">
              <p className="text-[15px] text-blanc">Passer Organisateur</p>
              <p className="mt-1 text-[13px] leading-relaxed text-fumee">
                Autant d’événements que vous voulez, un profil organisateur, des sponsors, la
                billetterie et le tableau de bord complet.{' '}
                {organisateur.evenements_a_venir >= organisateur.gratuits &&
                  'Vous avez déjà un événement à venir : c’est le moment.'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-match-gradient font-serif text-[24px] leading-tight">
                {euros(organisateur.prix_mensuel_centimes)}
                <span className="font-sans text-[13px] text-fumee"> /mois</span>
              </p>
              <p className="text-[12px] text-fumee">facturés {euros(organisateur.prix_annuel_centimes)} à l’année</p>
              <Btn variant="primary" className="mt-2" onClick={() => void souscrire()}>
                Devenir Organisateur
              </Btn>
            </div>
          </div>
        </Card>
      )}
      {message && <p className="mt-3 text-[13px]" style={{ color: 'var(--em-erreur)' }}>{message}</p>}
    </section>
  );
}

/* ————————————————— Créer / modifier ————————————————— */

const versLocal = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function FormulaireEvenement({
  existant,
  organisateur,
  onClose,
  onFini,
}: {
  existant: EvenementCree | null;
  organisateur: EspaceOrganisateur;
  onClose: () => void;
  onFini: () => void;
}) {
  const [nom, setNom] = useState(existant?.nom ?? '');
  const [categorie, setCategorie] = useState<CategorieEvenement>(existant?.categorie ?? 'networking');
  const [sous, setSous] = useState<string>(existant?.sous_categorie ?? 'jouer');
  const [description, setDescription] = useState(existant?.description ?? '');
  const [lieuNom, setLieuNom] = useState(existant?.lieu_nom ?? '');
  const [lieuVille, setLieuVille] = useState(existant?.lieu_ville ?? '');
  const [adresse, setAdresse] = useState(existant?.adresse ?? '');
  const [codePostal, setCodePostal] = useState(existant?.code_postal ?? '');
  const [imageUrl, setImageUrl] = useState(existant?.image_url ?? '');
  const [lienBilletterie, setLienBilletterie] = useState(existant?.lien_billetterie ?? '');
  const [siteWeb, setSiteWeb] = useState(existant?.site_web ?? '');
  const [debut, setDebut] = useState(existant ? versLocal(existant.debut_le) : '');
  const [fin, setFin] = useState(existant ? versLocal(existant.fin_le) : '');
  const [publique, setPublique] = useState(existant?.visibilite_publique ?? true);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const enregistrer = async () => {
    setOccupe(true);
    setErreur(null);
    const corps = {
      nom: nom.trim(),
      categorie,
      sousCategorie: categorie === 'sport' ? sous : null,
      description: description.trim() || null,
      lieuNom: lieuNom.trim() || null,
      lieuVille: lieuVille.trim() || null,
      adresse: adresse.trim() || null,
      codePostal: codePostal.trim() || null,
      imageUrl: imageUrl.trim() || null,
      lienBilletterie: lienBilletterie.trim() || null,
      siteWeb: siteWeb.trim() || null,
      debutLe: debut ? new Date(debut).toISOString() : undefined,
      finLe: fin ? new Date(fin).toISOString() : undefined,
      visibilitePublique: publique,
    };
    const r = existant
      ? await envoyer(`/api/mes-evenements/${existant.id}`, corps, 'PUT')
      : await envoyer('/api/mes-evenements', corps);
    setOccupe(false);
    if (r.erreur) {
      setErreur(r.erreur);
      return;
    }
    onFini();
  };

  const archiver = async () => {
    if (!existant) return;
    setOccupe(true);
    await envoyer(`/api/mes-evenements/${existant.id}`, { statut: 'archive' }, 'PUT');
    setOccupe(false);
    onFini();
  };

  return (
    <Modal title={existant ? 'Modifier l’événement' : 'Créer un événement'} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <Input label="Nom" value={nom} onChange={setNom} placeholder="Afterwork des entrepreneurs liégeois" />
        <div>
          <span className="label-caps block text-fumee">Catégorie</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {CATEGORIES_EVENEMENT.map((c) => (
              <Chip key={c.cle} active={categorie === c.cle} onClick={() => setCategorie(c.cle)}>
                {c.libelle}
              </Chip>
            ))}
          </div>
        </div>
        {categorie === 'sport' && (
          <Select
            label="On y joue, ou on regarde ?"
            value={sous}
            onChange={setSous}
            options={[
              { valeur: 'jouer', texte: 'On joue — tournoi, match, sortie' },
              { valeur: 'regarder', texte: 'On regarde — loge, écran géant, tribune' },
            ]}
          />
        )}
        <TextArea label="Description" value={description} onChange={setDescription} rows={3} placeholder="Ce qui s’y passe, pour qui, et pourquoi venir." />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Lieu" value={lieuNom} onChange={setLieuNom} placeholder="Palais des Congrès" />
          <Input label="Ville" value={lieuVille} onChange={setLieuVille} placeholder="Liège" />
          <Input label="Adresse" value={adresse} onChange={setAdresse} placeholder="Esplanade de l’Europe 2" />
          <Input label="Code postal" value={codePostal} onChange={setCodePostal} placeholder="4020" />
        </div>
        <p className="-mt-2 text-[12px] text-fumee">L’adresse place l’événement sur la carte et dans « autour de vous ».</p>
        <Input label="Image (URL)" value={imageUrl} onChange={setImageUrl} placeholder="https://…/affiche.jpg" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Lien de billetterie externe" value={lienBilletterie} onChange={setLienBilletterie} placeholder="https://passpass.be/…" />
          <Input label="Site de l’événement" value={siteWeb} onChange={setSiteWeb} placeholder="https://…" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Début" value={debut} onChange={setDebut} type="datetime-local" />
          <Input label="Fin" value={fin} onChange={setFin} type="datetime-local" />
        </div>
        <label className="flex items-start gap-3 text-[14px] text-blanc">
          <input type="checkbox" checked={publique} onChange={(e) => setPublique(e.target.checked)} className="mt-1" />
          <span>
            Inscription libre
            <span className="block text-[12px] text-fumee">
              Visible dans la liste des événements à rejoindre et recommandé aux profils compatibles.
              Décoché : sur invitation seulement.
            </span>
          </span>
        </label>

        {!existant && !organisateur.peut_creer_gratuitement && (
          <p className="text-[13px] leading-relaxed text-fumee">
            Vous avez déjà un événement à venir. Celui-ci demandera la formule Organisateur ({euros(organisateur.prix_mensuel_centimes)}/mois,
            facturés à l’année).
          </p>
        )}
        {erreur && <p className="text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Btn variant="primary" onClick={() => void enregistrer()} disabled={occupe}>
            {occupe ? 'Un instant…' : existant ? 'Enregistrer' : 'Créer l’événement'}
          </Btn>
          {existant && existant.statut !== 'archive' && (
            <Btn onClick={() => void archiver()} disabled={occupe}>Archiver</Btn>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function BoutonEntrer({ participantId }: { participantId: string }) {
  const [enCours, setEnCours] = useState(false);
  return (
    <button
      className="btn-creme !min-h-0 px-4 py-2 text-[13px]"
      disabled={enCours}
      onClick={() => {
        setEnCours(true);
        void envoyer(`/api/compte/participations/${participantId}/entrer`, {}).then((r) => {
          if (r.erreur) setEnCours(false);
          else location.assign('/');
        });
      }}
    >
      {enCours ? '…' : 'Entrer dans l’événement'}
    </button>
  );
}
